import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MentionsLegalesPage = () => {
  const [legalInfo, setLegalInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLegalInfo = async () => {
      try {
        const response = await axios.get(`${API}/legal-info`);
        setLegalInfo(response.data);
      } catch (error) {
        console.error('Error fetching legal info:', error);
        // Fallback data
        setLegalInfo({
          company_name: "CONFORT HOTE",
          legal_form: "SASU - Société par Actions Simplifiée Unipersonnelle",
          capital: "1 000,00 €",
          siret: "931 566 574 00014",
          siren: "931 566 574",
          rcs: "Rennes",
          tva: "FR45931566574",
          address: "210 Lieu Dit La Roulerie, 35500 Balazé",
          president: "Gérard Hany",
          email: "contact@confort-hote.fr",
          phone: "06 89 22 44 02",
          creation_date: "24/07/2024"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLegalInfo();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="mentions-legales-page">
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Informations légales
            </p>
            
            <h1 className="text-4xl sm:text-5xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-12">
              Mentions légales
            </h1>

            <div className="space-y-12">
              {/* Éditeur */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Éditeur du site</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light">
                  <p><span className="text-[#F5F5F5]">Raison sociale :</span> {legalInfo.company_name}</p>
                  <p><span className="text-[#F5F5F5]">Forme juridique :</span> {legalInfo.legal_form}</p>
                  <p><span className="text-[#F5F5F5]">Capital social :</span> {legalInfo.capital}</p>
                  <p><span className="text-[#F5F5F5]">SIRET :</span> {legalInfo.siret}</p>
                  <p><span className="text-[#F5F5F5]">SIREN :</span> {legalInfo.siren}</p>
                  <p><span className="text-[#F5F5F5]">RCS :</span> {legalInfo.rcs}</p>
                  <p><span className="text-[#F5F5F5]">TVA Intracommunautaire :</span> {legalInfo.tva}</p>
                  <p><span className="text-[#F5F5F5]">Siège social :</span> {legalInfo.address}</p>
                  <p><span className="text-[#F5F5F5]">Président :</span> {legalInfo.president}</p>
                  <p><span className="text-[#F5F5F5]">Date de création :</span> {legalInfo.creation_date}</p>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Contact</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light">
                  <p><span className="text-[#F5F5F5]">Email :</span> <a href={`mailto:${legalInfo.email}`} className="text-[#C5A059] hover:underline">{legalInfo.email}</a></p>
                  <p><span className="text-[#F5F5F5]">Téléphone :</span> <a href={`tel:${legalInfo.phone.replace(/\s/g, '')}`} className="text-[#C5A059] hover:underline">{legalInfo.phone}</a></p>
                </div>
              </div>

              {/* Hébergement */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Hébergement</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light">
                  <p>Le site est hébergé par Netlify Inc.</p>
                  <p>44 Montgomery Street, Suite 300, San Francisco, California 94104</p>
                </div>
              </div>

              {/* Propriété intellectuelle */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Propriété intellectuelle</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light leading-relaxed">
                  <p>
                    L'ensemble du contenu de ce site (textes, images, vidéos, logos, graphismes) 
                    est la propriété exclusive de {legalInfo.company_name}, sauf mention contraire.
                  </p>
                  <p>
                    Toute reproduction, distribution, modification, adaptation, retransmission ou 
                    publication de ces éléments est strictement interdite sans l'accord écrit de 
                    {legalInfo.company_name}.
                  </p>
                </div>
              </div>

              {/* Données personnelles */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Protection des données personnelles</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light leading-relaxed">
                  <p>
                    Conformément au Règlement Général sur la Protection des Données (RGPD) et à la 
                    loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, 
                    de suppression et de portabilité de vos données personnelles.
                  </p>
                  <p>
                    Les données collectées via les formulaires de ce site sont utilisées uniquement 
                    pour répondre à vos demandes et ne sont jamais transmises à des tiers sans votre 
                    consentement explicite.
                  </p>
                  <p>
                    Pour exercer vos droits ou pour toute question relative à vos données personnelles, 
                    vous pouvez nous contacter à l'adresse : <a href={`mailto:${legalInfo.email}`} className="text-[#C5A059] hover:underline">{legalInfo.email}</a>
                  </p>
                </div>
              </div>

              {/* Cookies */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Cookies</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light leading-relaxed">
                  <p>
                    Ce site utilise des cookies techniques nécessaires à son bon fonctionnement. 
                    Ces cookies ne collectent aucune donnée personnelle et ne sont pas utilisés 
                    à des fins de suivi ou de publicité.
                  </p>
                </div>
              </div>

              {/* Responsabilité */}
              <div className="bg-[#0A0A0A] border border-[#262626] p-8">
                <h2 className="text-xl font-playfair text-[#C5A059] mb-6">Limitation de responsabilité</h2>
                <div className="space-y-3 text-[#A3A3A3] font-light leading-relaxed">
                  <p>
                    Les informations contenues sur ce site sont données à titre indicatif et sont 
                    susceptibles d'évoluer. {legalInfo.company_name} ne saurait être tenue responsable 
                    des erreurs, omissions ou résultats qui pourraient être obtenus par un mauvais 
                    usage de ces informations.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MentionsLegalesPage;
